import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { fetchOrder } from "@/lib/api";

// Convert a UK phone to a tel: URI so the QR triggers a call when scanned.
const phoneToTelUri = (phone) => {
    if (!phone) return "";
    const digits = String(phone).replace(/[^\d+]/g, "");
    if (digits.startsWith("+")) return `tel:${digits}`;
    if (digits.startsWith("00")) return `tel:+${digits.slice(2)}`;
    if (digits.startsWith("0")) return `tel:+44${digits.slice(1)}`;
    return `tel:${digits}`;
};

const PrintSlips = () => {
    const { id } = useParams();
    const [order, setOrder] = useState(null);

    useEffect(() => {
        fetchOrder(id).then(setOrder);
    }, [id]);

    useEffect(() => {
        if (order) {
            // Auto-open print dialog after a beat
            const t = setTimeout(() => window.print(), 300);
            return () => clearTimeout(t);
        }
    }, [order]);

    if (!order) {
        return (
            <div className="min-h-screen bg-white text-black p-8 font-mono">
                Loading order…
            </div>
        );
    }

    // group lines by brand
    const grouped = order.lines.reduce((acc, l) => {
        const key = l.brand_id;
        if (!acc[key]) {
            acc[key] = {
                brand_name: l.brand_name,
                brand_token: l.brand_token,
                lines: [],
                total: 0,
            };
        }
        acc[key].lines.push(l);
        acc[key].total += l.price * l.qty;
        return acc;
    }, {});

    const createdAt = new Date(order.created_at);

    return (
        <div className="bg-white text-black min-h-screen">
            <div className="no-print sticky top-0 bg-white border-b-2 border-black px-6 py-4 flex items-center justify-between">
                <h1 className="font-mono uppercase tracking-widest text-sm">
                    Order #{order.order_number} · {Object.keys(grouped).length} slip(s)
                </h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => window.print()}
                        className="px-4 py-2 bg-black text-white font-mono uppercase text-xs tracking-widest"
                        data-testid="print-button"
                    >
                        Print
                    </button>
                    <button
                        onClick={() => window.close()}
                        className="px-4 py-2 border-2 border-black font-mono uppercase text-xs tracking-widest"
                    >
                        Close
                    </button>
                </div>
            </div>

            <div className="max-w-md mx-auto p-6 print:p-0 print:max-w-none">
                {Object.values(grouped).map((g, i) => (
                    <div
                        key={i}
                        className="print-slip bg-white text-black p-6 mb-8 border-2 border-black print:border-0 print:mb-0"
                        data-testid={`print-slip-${g.brand_token}`}
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                        <div className="text-center mb-4">
                            <div className="text-xs uppercase tracking-widest">
                                Naami Kitchens
                            </div>
                            <div className="text-3xl font-black mt-1">{g.brand_token}</div>
                            <div className="text-base font-bold uppercase">{g.brand_name}</div>
                        </div>

                        <div className="border-t-2 border-b-2 border-black py-2 my-3 text-center">
                            <div className="text-xs uppercase">Order</div>
                            <div className="text-4xl font-black">#{order.order_number}</div>
                        </div>

                        <div className="text-xs mb-3 grid grid-cols-2 gap-1">
                            <div>
                                <div className="uppercase opacity-60">Time</div>
                                <div className="font-bold">
                                    {createdAt.toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="uppercase opacity-60">Source</div>
                                <div className="font-bold uppercase">{order.source}</div>
                            </div>
                        </div>

                        {order.customer_name && (
                            <div className="text-xs mb-3">
                                <span className="uppercase opacity-60">Name: </span>
                                <span className="font-bold uppercase">
                                    {order.customer_name}
                                </span>
                            </div>
                        )}

                        {order.order_type === "delivery" && order.delivery && (
                            <div className="border-4 border-black p-2 mb-3">
                                <div className="font-black text-base uppercase tracking-widest text-center mb-1 bg-black text-white py-0.5">★ DELIVERY ★</div>
                                {order.customer_name && (
                                    <div className="text-sm font-black uppercase mb-1">{order.customer_name}</div>
                                )}
                                <div className="text-sm uppercase font-bold leading-tight">
                                    {order.delivery.address_line_1}<br/>
                                    {order.delivery.address_line_2 && <>{order.delivery.address_line_2}<br/></>}
                                    {order.delivery.city && <>{order.delivery.city}<br/></>}
                                    {order.delivery.postcode}
                                </div>
                                <div className="text-base font-black mt-1">☎ {order.delivery.phone}</div>
                                {order.delivery.phone && (
                                    <div className="mt-1 flex flex-col items-center" data-testid="slip-phone-qr">
                                        <QRCodeSVG
                                            value={phoneToTelUri(order.delivery.phone)}
                                            size={160}
                                            level="H"
                                            includeMargin={true}
                                        />
                                        <div className="font-mono text-[9px] uppercase tracking-widest mt-0.5">Scan to call</div>
                                    </div>
                                )}
                                {order.delivery.instructions && (
                                    <div className="mt-2 border-2 border-black">
                                        <div className="font-black text-[10px] uppercase tracking-widest text-center border-b-2 border-black py-0.5 bg-black text-white">!! DRIVER INSTRUCTIONS !!</div>
                                        <div className="text-sm font-black uppercase p-1.5 leading-tight">
                                            {order.delivery.instructions}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {order.order_type === "collection" && (
                            <div className="border-2 border-black p-1 mb-3 text-center">
                                <div className="font-black text-sm uppercase tracking-widest">COLLECTION</div>
                            </div>
                        )}

                        <div className="border-t-2 border-black pt-2 mb-3">
                            <div className="text-xs uppercase mb-2 font-bold">Items</div>
                            {(() => {
                                // Group this brand's lines by category, preserving order of appearance.
                                const byCat = {};
                                const order2 = [];
                                for (const l of g.lines) {
                                    const c = (l.category || "").trim() || "Other";
                                    if (!byCat[c]) { byCat[c] = []; order2.push(c); }
                                    byCat[c].push(l);
                                }
                                return order2.map((cat) => (
                                    <div key={cat} className="mb-2">
                                        <div className="text-[11px] font-black uppercase tracking-widest border-b border-black pb-0.5 mb-1">
                                            -- {cat} --
                                        </div>
                                        {byCat[cat].map((l) => (
                                            <div key={`${l.item_id}-${(l.modifiers||[]).map(m=>m.option_name).join('|')}`}>
                                                <div className="flex justify-between text-sm font-bold mb-1">
                                                    <span>{l.qty}× {l.name.toUpperCase()}</span>
                                                    <span>£{(l.price * l.qty).toFixed(2)}</span>
                                                </div>
                                                {l.modifiers?.length > 0 && (
                                                    <div className="pl-3 mb-1 text-[11px] font-mono">
                                                        {l.modifiers.map((m, i) => (
                                                            <div key={i}>→ {m.option_name.toUpperCase()}</div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ));
                            })()}
                        </div>

                        {(() => {
                            // Prefer per-brand kitchen note (multi-brand baskets); fall back to global.
                            const bId = g.lines[0]?.brand_id;
                            const bNote = (order.brand_notes || {})[bId] || null;
                            const showNote = bNote || (!Object.keys(order.brand_notes || {}).length && order.notes);
                            return showNote ? (
                                <div className="border-2 border-black p-2 mb-3 text-xs">
                                    <div className="font-black uppercase mb-1">!! Kitchen note</div>
                                    <div className="font-bold uppercase">{bNote || order.notes}</div>
                                </div>
                            ) : null;
                        })()}

                        <div className="border-t-2 border-black pt-2 flex justify-between font-black text-base">
                            <span>BRAND TOTAL</span>
                            <span>£{g.total.toFixed(2)}</span>
                        </div>

                        <div
                            className={`text-center mt-3 py-2 ${
                                order.pay_method === "cash"
                                    ? "bg-black text-white"
                                    : "border-2 border-black"
                            } font-black uppercase tracking-widest text-sm`}
                        >
                            {order.pay_method === "cash"
                                ? "!! CASH · COLLECT AT COUNTER"
                                : "Card · Paid"}
                        </div>

                        <div className="text-center text-xs mt-3 uppercase opacity-60">
                            Slip {i + 1} of {Object.keys(grouped).length}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PrintSlips;
